// resources/js/src/components/community/components/profile/ProfileActions.tsx
import { useState } from "react";
import { Icon } from "@iconify/react";
import ReportUserModal from "./ReportUserModal";
import type { ProfileState } from "../../../../../services/profile.api";
import { friendshipApi } from "../../../../../services/friendship.api";
import { useProfileStore } from "../../../../../store/profileStore";

type Props = {
  state: ProfileState;
  userId: string;
  userName: string;
  onActionDone: () => void;
};

/* ============================================================
   Button Variants (Premium)
   ============================================================ */
const btnBase =
  "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-deepSlate";

const btnPrimary = `${btnBase} bg-gradient-to-r from-primary to-primary/90 text-midnight_text shadow-lg shadow-primary-600/25 hover:shadow-xl hover:shadow-primary-600/40 hover:from-primary-700 hover:to-primary-600 focus:ring-primary-500`;

const btnSecondary = `${btnBase} bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 focus:ring-gray-400`;

const btnDanger = `${btnBase} bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 border border-red-200 dark:border-red-800/50 focus:ring-red-500`;

const btnGhost = `${btnBase} bg-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 focus:ring-gray-400`;

/* ============================================================
   Component
   ============================================================ */
export default function ProfileActions({
  state,
  userId,
  userName,
  onActionDone,
}: Props) {
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState("");
  const [reportOpen, setReportOpen] = useState(false);

  const { setStateOptimistic, rollbackState } = useProfileStore();

  const run = async (
    fn: () => Promise<any>,
    optimisticState?: ProfileState
  ) => {
    const oldState = state;
    setIsBusy(true);
    setError("");

    if (optimisticState) {
      setStateOptimistic(optimisticState);
    }

    try {
      await fn();
      onActionDone();
    } catch (e: any) {
      if (optimisticState) {
        rollbackState(oldState);
      }
      setError(e?.message ?? "Something went wrong.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleUnfriend = () => {
    const confirmed = window.confirm(
      "Remove this friend? You can send a new friend request later."
    );
    if (!confirmed) return;
    run(() => friendshipApi.removeFriend(userId), "stranger");
  };

  const handleBlock = () => {
    const confirmed = window.confirm(
      "Block this user? They won't be able to message you or see your profile."
    );
    if (!confirmed) return;
    run(() => friendshipApi.block(userId), "blocked_by_me");
  };

  if (!state) return null;

  /* ============================================================
     Render
     ============================================================ */
  return (
    <>
      <div className="flex flex-col items-end gap-3">
        {/* ===== Actions Bar ===== */}
        <div className="flex flex-wrap items-center gap-2">
          {/* ---------- owner ---------- */}
          {state === "owner" && (
            <>
              <button className={btnPrimary}>
                <Icon icon="mdi:pencil-outline" className="text-lg" />
                Edit Profile
              </button>
              <button className={btnSecondary}>
                <Icon icon="mdi:cog-outline" className="text-lg" />
                Settings
              </button>
            </>
          )}

          {/* ---------- friend ---------- */}
          {state === "friend" && (
            <>
              <button className={btnPrimary}>
                <Icon icon="mdi:message-outline" className="text-lg" />
                Message
              </button>
              <button
                className={btnSecondary}
                disabled={isBusy}
                onClick={handleUnfriend}
              >
                <Icon icon="mdi:account-minus-outline" className="text-lg" />
                {isBusy ? "..." : "Unfriend"}
              </button>
              <button
                className={btnDanger}
                disabled={isBusy}
                onClick={handleBlock}
              >
                <Icon icon="mdi:block-helper" className="text-lg" />
                {isBusy ? "..." : "Block"}
              </button>
            </>
          )}

          {/* ---------- pending_outgoing ---------- */}
          {state === "pending_outgoing" && (
            <>
              <button
                className={btnSecondary}
                disabled={isBusy}
                onClick={() =>
                  run(() => friendshipApi.cancelRequest(userId), "stranger")
                }
              >
                <Icon icon="mdi:close-circle-outline" className="text-lg" />
                {isBusy ? "..." : "Cancel Request"}
              </button>
              <button
                className={btnDanger}
                disabled={isBusy}
                onClick={handleBlock}
              >
                <Icon icon="mdi:block-helper" className="text-lg" />
                {isBusy ? "..." : "Block"}
              </button>
            </>
          )}

          {/* ---------- pending_incoming ---------- */}
          {state === "pending_incoming" && (
            <>
              <button
                className={btnPrimary}
                disabled={isBusy}
                onClick={() =>
                  run(() => friendshipApi.acceptRequest(userId), "friend")
                }
              >
                <Icon icon="mdi:account-check-outline" className="text-lg" />
                {isBusy ? "..." : "Accept"}
              </button>
              <button
                className={btnSecondary}
                disabled={isBusy}
                onClick={() =>
                  run(() => friendshipApi.declineRequest(userId), "stranger")
                }
              >
                <Icon icon="mdi:account-cancel-outline" className="text-lg" />
                {isBusy ? "..." : "Decline"}
              </button>
              <button
                className={btnDanger}
                disabled={isBusy}
                onClick={handleBlock}
              >
                <Icon icon="mdi:block-helper" className="text-lg" />
                {isBusy ? "..." : "Block"}
              </button>
            </>
          )}

          {/* ---------- stranger ---------- */}
          {state === "stranger" && (
            <>
              <button
                className={btnPrimary}
                disabled={isBusy}
                onClick={() =>
                  run(
                    () => friendshipApi.sendRequest(userId),
                    "pending_outgoing"
                  )
                }
              >
                <Icon icon="mdi:account-plus-outline" className="text-lg" />
                {isBusy ? "..." : "Add Friend"}
              </button>
              <button className={btnSecondary}>
                <Icon icon="mdi:message-outline" className="text-lg" />
                Message
              </button>
              <button
                className={btnDanger}
                disabled={isBusy}
                onClick={handleBlock}
              >
                <Icon icon="mdi:block-helper" className="text-lg" />
                {isBusy ? "..." : "Block"}
              </button>
            </>
          )}

          {/* ---------- blocked_by_me ---------- */}
          {state === "blocked_by_me" && (
            <button
              className={btnDanger}
              disabled={isBusy}
              onClick={() =>
                run(() => friendshipApi.unblock(userId), "stranger")
              }
            >
              <Icon icon="mdi:lock-open-outline" className="text-lg" />
              {isBusy ? "..." : "Unblock"}
            </button>
          )}

          {/* ---------- blocked_by_them / not_found / banned => no buttons ---------- */}
        </div>

        {/* ===== Error ===== */}
        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50">
            <Icon
              icon="mdi:alert-circle-outline"
              className="text-red-500 text-lg shrink-0"
            />
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* ===== Report Link ===== */}
        {(state === "stranger" ||
          state === "friend" ||
          state === "pending_incoming") && (
          <button
            onClick={() => setReportOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors duration-200"
          >
            <Icon icon="mdi:flag-outline" className="text-sm" />
            Report User
          </button>
        )}
      </div>

      {/* ===== Report Modal ===== */}
      <ReportUserModal
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        userId={userId}
        userName={userName}
      />
    </>
  );
}